import { useState, useRef, useEffect } from 'react'
import { CameraIcon, RefreshCw, AlertCircle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetTrigger,
  SheetClose
} from '@components/ui/sheet'
import { Button } from '@components/ui/button'
import { Skeleton } from '@components/ui/skeleton'
import { Alert, AlertDescription } from '@components/ui/alert'

const CameraCapture = ({
  onCapture,
  disabled = false
}) => {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [cameraError, setCameraError] = useState(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)

  // Clean up camera stream when component unmounts
  useEffect(() => {
    return () => {
      stopCameraStream()
    }
  }, [])

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
  }

  const startCameraStream = async () => {
    try {
      setIsLoading(true)
      setCameraError(null)

      if (streamRef.current) {
        stopCameraStream()
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play().catch(() => {
          setCameraError('Unable to access your camera. Please check your browser permissions and try again.')
          setIsLoading(false)
        })

        // Wait for video to be ready to play
        videoRef.current.onloadeddata = () => {
          setIsLoading(false)
        }
      }
    } catch {
      setCameraError('Unable to access your camera. Please check your browser permissions and try again.')
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    stopCameraStream()
    setOpen(false)
  }

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current
      const canvas = canvasRef.current

      // Make sure dimensions match the actual video
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      const context = canvas.getContext('2d')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)

      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.png`, { type: 'image/png' })

          if (onCapture && typeof onCapture === 'function') {
            onCapture(file)
          }

          handleClose()
        }
      }, 'image/png')
    }
  }

  return (
    <Sheet
      open={open} onOpenChange={(newOpen) => {
        setOpen(newOpen)
        if (newOpen) {
          startCameraStream()
        } else {
          stopCameraStream()
        }
      }}
    >
      <SheetTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          disabled={disabled}
          className='hidden md:flex !size-8'
          title='Take a photo'
        >
          <CameraIcon className='size-4.5' />
        </Button>
      </SheetTrigger>

      <SheetContent className='sm:max-w-md'>
        <SheetHeader>
          <SheetTitle>Take a Photo</SheetTitle>
        </SheetHeader>

        <div className='p-4'>
          {cameraError
            ? (
              <Alert>
                <AlertCircle className='h-4 w-4' />
                <AlertDescription className='mb-3'>{cameraError}
                  <Button
                    className='mt-2'
                    onClick={startCameraStream}
                    variant='outline'
                  >
                    <RefreshCw className='size-4' />
                    Try again
                  </Button>
                </AlertDescription>
              </Alert>
              )
            : (
              <div className='relative'>
                {isLoading && (
                  <div className='w-full aspect-video rounded-md overflow-hidden'>
                    <Skeleton className='w-full h-full' />
                  </div>
                )}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className={`w-full h-auto rounded-md ${isLoading ? 'hidden' : 'block'}`}
                />
                <canvas ref={canvasRef} className='hidden' />
              </div>
              )}
        </div>

        <SheetFooter className='flex flex-row gap-2 justify-end'>
          <SheetClose asChild>
            <Button
              type='button'
              variant='ghost'
              onClick={stopCameraStream}
            >
              Cancel
            </Button>
          </SheetClose>
          <Button
            type='button'
            onClick={capturePhoto}
            disabled={!!cameraError || isLoading}
          >
            <CameraIcon className='size-4 mr-2' />
            Capture
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default CameraCapture
